import { Flex, Text, Avatar, useMantineTheme, Collapse, ActionIcon } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	ConnectingLine,
	DocumentCard,
	Dot,
	HistoryEventContainer,
	EventContent,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/styled';
import {
	EventDocument,
	HistoryEventResult,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { useState } from 'react';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	historyEvents: HistoryEventResult[] | undefined;
	onDocumentClick?: (eventDocument: EventDocument) => void;
}

export const HistoryEvents = ({ historyEvents, onDocumentClick }: Props) => {
	const theme = useMantineTheme();
	const tMipManage = useTranslationMipManage();
	const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());

	const toggleShowingComment = (index: number) => {
		setExpandedComments((prev) => {
			const commentIndexes = new Set(prev);
			if (commentIndexes.has(index)) {
				commentIndexes.delete(index);
			} else {
				commentIndexes.add(index);
			}
			return commentIndexes;
		});
	};

	const documentClickHandler = (eventDocument: EventDocument) =>
		onDocumentClick && eventDocument.fileId ? () => onDocumentClick(eventDocument) : undefined;

	if (!historyEvents?.length) {
		return (
			<EmptyState
				icon="es-post-box"
				iconSize="sm"
				description={tMipManage('kyc.no_history_2')}
			/>
		);
	}

	return (
		<HistoryEventContainer>
			{historyEvents?.map((event, index) => (
				<FlexExtended
					gap="0.5rem"
					w="100%"
					key={`history-event-${index}-${event.timestamp}`}
					data-testid={`${TEST_ELEMENT_IDS.ACTIVITY_HISTORY_EVENT}-${event.action ?? 'unknown'}`}
				>
					{/* Avatar and connecting line */}
					<FlexExtended direction="column" align="center">
						<Avatar size={32} radius="xl" color="gray" bg={theme.colors.gray[3]}>
							<Icon variant="user-profile" size="sm" />
						</Avatar>
						{index < historyEvents.length - 1 && <ConnectingLine />}
					</FlexExtended>

					<EventContent>
						{/* Action and timestamp row */}
						<Flex align="flex-start" gap="0.5rem" wrap="wrap">
							<Flex align="center" gap="0.5rem">
								<Text size="14px" fw={600} c={theme.colors.dark[8]} lh="20px">
									{tMipManage(
										`kyc.history.action.${event.action || 'unknown_action'}`
									)}
								</Text>
								<Text size="14px" fw={600} c={theme.colors.dark[8]} lh="20px">
									{event.actor}
								</Text>
							</Flex>
							<Flex align="center" gap="0.5rem">
								<Dot />
								<Text size="14px" fw={400} c={theme.colors.dark[8]} lh="20px">
									{event.timestamp}
								</Text>
							</Flex>
						</Flex>

						{/* Browser info */}
						<Text size="14px" fw={400} c={theme.colors.dark[0]} lh="20px">
							{event.browserInfo}
						</Text>

						{event.signingMethodLabel && (
							<Text size="14px" fw={400} c={theme.colors.dark[0]} lh="20px">
								{tMipManage('kyc.history.signed_via', {
									method: event.signingMethodLabel,
								})}
								{event.complianceLabel ? ` (${event.complianceLabel})` : ''}
							</Text>
						)}

						{/* Document card (current version) */}
						{event.document && (
							<DocumentCard
								data-testid="history-event-document-card"
								onClick={documentClickHandler(event.document)}
								style={
									onDocumentClick && event.document.fileId
										? { cursor: 'pointer' }
										: undefined
								}
							>
								<Icon variant="document-v3-rounded" size="lg" />

								<Text
									size="14px"
									fw={400}
									c={theme.colors.blue[8]}
									lh="20px"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										flex: 1,
									}}
								>
									{event.document.fileName}
								</Text>
								{event.priorDocument && (
									<Text
										size="11px"
										fw={600}
										c={theme.colors.green[7]}
										lh="16px"
										style={{ whiteSpace: 'nowrap' }}
									>
										current
									</Text>
								)}
							</DocumentCard>
						)}

						{/* Prior version (replacement events) */}
						{event.priorDocument && (
							<DocumentCard
								data-testid="history-event-prior-document-card"
								onClick={documentClickHandler(event.priorDocument)}
								style={
									onDocumentClick && event.priorDocument.fileId
										? { cursor: 'pointer' }
										: undefined
								}
							>
								<Icon variant="document-v3-rounded" size="lg" />

								<Text
									size="14px"
									fw={400}
									c={theme.colors.blue[8]}
									lh="20px"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										flex: 1,
									}}
								>
									{event.priorDocument.fileName}
								</Text>
								<Text
									size="11px"
									fw={600}
									c={theme.colors.gray[7]}
									lh="16px"
									style={{ whiteSpace: 'nowrap' }}
								>
									prior
								</Text>
							</DocumentCard>
						)}

						{event.comment && (
							<Flex direction="column" gap="xs">
								<Flex align="center" gap="xs">
									<Text size="14px" fw={400} c={theme.colors.dark[0]} lh="20px">
										{tMipManage('kyc.history.comment')}:
									</Text>
									<ActionIcon
										variant="subtle"
										size="sm"
										onClick={() => toggleShowingComment(index)}
										style={{ color: theme.colors.blue[6] }}
									>
										<Icon
											variant={
												expandedComments.has(index)
													? 'arrow-up-v3'
													: 'arrow-down-v3'
											}
											size="sm"
										/>
									</ActionIcon>
								</Flex>
								<Collapse in={expandedComments.has(index)}>
									<Text
										size="14px"
										fw={400}
										c={theme.colors.dark[0]}
										lh="20px"
										style={{
											wordBreak: 'break-word',
											paddingLeft: '1rem',
										}}
									>
										{event.comment}
									</Text>
								</Collapse>
							</Flex>
						)}
					</EventContent>
				</FlexExtended>
			))}
		</HistoryEventContainer>
	);
};
